import {
  buildActivitySubjectRouteId,
  normalizeActivitySubjectRouteId,
} from '@shared/types/activity-subject-routes';

export interface SubjectThreadRouteInput {
  subjectId?: string | null;
  subjectKey: string | null;
  subjectKind: string;
  subjectLabel: string;
}

export interface SubjectThreadRouteState {
  subjectId: string;
  subjectKey: string | null;
  subjectKind: string;
  subjectLabel: string;
}

const FALLBACK_KIND = 'subject';

const normalizeRoutePart = (value: string | null | undefined): string =>
  typeof value === 'string' ? value.trim() : '';

const humanizeRouteLabel = (value: string): string => {
  const parts = value.split(':').filter(Boolean);
  const last = parts.length > 0 ? (parts[parts.length - 1] ?? value) : value;
  const words = last.split('-').join(' ').split('_').join(' ').split(' ').filter(Boolean);
  if (words.length === 0) return value;

  return words
    .map(
      (word: string) => `${word.slice(0, 1).toLocaleUpperCase('fr-FR')}${word.slice(1)}`
    )
    .join(' ');
};

const inferSubjectKind = (legacySubjectKey: string): string => {
  const [kind] = legacySubjectKey.split(':');
  return normalizeRoutePart(kind) || FALLBACK_KIND;
};

export const buildSubjectThreadRef = (subject: SubjectThreadRouteInput): string => {
  return buildActivitySubjectRouteId(subject);
};

export const buildSubjectThreadPath = (subject: SubjectThreadRouteInput): string => {
  const routeId = buildSubjectThreadRef(subject);
  return `/threads/${encodeURIComponent(routeId)}`;
};

export const resolveSubjectThreadRoute = (
  subjectIdParam: string | undefined,
  searchParams: URLSearchParams
): SubjectThreadRouteState | null => {
  const decodedRef = subjectIdParam ? decodeURIComponent(subjectIdParam).trim() : '';
  const queryKey = normalizeRoutePart(searchParams.get('key'));
  const isLegacySubjectKeyPath = decodedRef.includes(':');
  const legacySubjectKey = isLegacySubjectKeyPath ? decodedRef : queryKey;
  const subjectId = isLegacySubjectKeyPath
    ? buildActivitySubjectRouteId({ subjectKey: decodedRef })
    : normalizeActivitySubjectRouteId(decodedRef);
  const subjectKind =
    normalizeRoutePart(searchParams.get('kind'))
    || (legacySubjectKey ? inferSubjectKind(legacySubjectKey) : FALLBACK_KIND);
  const subjectLabel =
    normalizeRoutePart(searchParams.get('label')) || humanizeRouteLabel(decodedRef);
  const subjectKey = legacySubjectKey || null;

  if (!decodedRef || !subjectId || !subjectKind || !subjectLabel) {
    return null;
  }

  return {
    subjectId,
    subjectKey,
    subjectKind,
    subjectLabel,
  };
};
